// Customer Requests page (ADR-179 P3, extended by ADR-186): the
// operator-only inbox for the notify-operator RPCs a customer's account
// owner/admin (RequestSeatChange/RequestCancellation) or any account role
// (RequestSupport, ADR-186's portal "Get help" affordance) can call.
// ADR-186 adds the acknowledge/resolve lifecycle this file's original
// header explicitly named as a documented follow-up ("Acknowledging or
// resolving a request would need its own RPC ... that is NOT in this
// build's proto") -- that gap is now closed.
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card chrome; data layer untouched by that
// pass. ADR-186 pass: kind filter, Acknowledge/Resolve buttons, status
// chips using Genesis's STATUS_COLOR vocabulary, acked/resolved metadata.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { acknowledgeCustomerRequest, listCustomerRequests, resolveCustomerRequest } from '@/lib/grpcweb/console-api'
import { formatTimestamp } from '@/lib/formatters'
import { useAuth } from '@/lib/auth/auth-context'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DARK, GREY_200, GREY_300, GREY_400, STATUS_COLOR, WHITE, errorBlockStyle } from '@/lib/genesis'

const STATUS_OPTIONS = ['', 'pending', 'acknowledged', 'resolved', 'dismissed'] as const
const KIND_OPTIONS = ['', 'seat_change', 'cancellation', 'support_request'] as const
// ADR-186 Phase 2: source distinguishes the two "Get help" submission
// channels -- the portal's customer-session RequestSupport (Phase 1) and
// the app's instance-channel SubmitInstanceSupportRequest (Phase 2).
const SOURCE_OPTIONS = ['', 'portal', 'app'] as const

function statusLabel(s: string): string {
  return s === '' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)
}

function kindLabel(k: string): string {
  if (k === '') return 'All kinds'
  if (k === 'seat_change') return 'Seat change'
  if (k === 'cancellation') return 'Cancellation'
  if (k === 'support_request') return 'Get help'
  return k
}

function sourceLabel(s: string): string {
  if (s === '') return 'All sources'
  if (s === 'portal') return 'Portal'
  if (s === 'app') return 'App'
  return s
}

// statusTone maps operator_requests.status to a Genesis STATUS_COLOR key.
// pending -> idle (nothing has happened yet), acknowledged -> info (in
// progress), resolved -> success (done), dismissed -> danger-adjacent
// (muted here as idle too -- dismissed is not a failure state, just a
// closed-without-action one; there is no dedicated "closed" tone in the
// Genesis vocabulary, so idle is the honest fit).
function statusTone(status: string): keyof typeof STATUS_COLOR {
  switch (status) {
    case 'acknowledged':
      return 'info'
    case 'resolved':
      return 'success'
    default:
      return 'idle'
  }
}

export function CustomerRequestsPage() {
  const { isAdmin } = useAuth()
  const [statusFilter, setStatusFilter] = useState('')
  const [kindFilter, setKindFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [resolveNoteDraft, setResolveNoteDraft] = useState<Record<string, string>>({})
  const [actionError, setActionError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-requests', statusFilter, kindFilter, sourceFilter],
    queryFn: () =>
      listCustomerRequests({
        statusFilter: statusFilter || undefined,
        kindFilter: kindFilter || undefined,
        sourceFilter: sourceFilter || undefined,
      }),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customer-requests'] })

  const ackMutation = useMutation({
    mutationFn: (requestId: string) => acknowledgeCustomerRequest(requestId),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Could not acknowledge this request.'),
  })

  const resolveMutation = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note: string }) => resolveCustomerRequest(requestId, note || undefined),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Could not resolve this request.'),
  })

  const requests = data?.getRequestsList() ?? []

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Customer Requests</h1>
      <p className="mb-4 max-w-2xl text-sm" style={{ color: GREY_400 }}>
        Seat-change, cancellation, and get-help requests submitted from the customer portal
        (ADR-179 section 7-D / ADR-186 — customers cannot self-cancel or self-change seats, and a
        bespoke help request is not a self-service action either; every entry here is a durable
        ticket for your team to acknowledge and resolve).
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="cr-status" className="text-xs" style={{ color: GREY_400 }}>
            Status
          </label>
          <select
            id="cr-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="glass-input px-2 py-1 text-sm"
            style={{ color: WHITE }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="cr-kind" className="text-xs" style={{ color: GREY_400 }}>
            Kind
          </label>
          <select
            id="cr-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="glass-input px-2 py-1 text-sm"
            style={{ color: WHITE }}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {kindLabel(k)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="cr-source" className="text-xs" style={{ color: GREY_400 }}>
            Source
          </label>
          <select
            id="cr-source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="glass-input px-2 py-1 text-sm"
            style={{ color: WHITE }}
          >
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {sourceLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {actionError && (
        <div className="mb-4 px-3 py-2" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>{actionError}</p>
        </div>
      )}

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading…</p>}
      {isError && (
        <div className="px-3 py-2" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load customer requests.</p>
        </div>
      )}
      {!isLoading && !isError && requests.length === 0 && (
        <p className="text-sm" style={{ color: GREY_400 }}>No requests match this filter.</p>
      )}

      {!isLoading && !isError && requests.length > 0 && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead style={{ color: GREY_400 }}>
              <tr>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Kind</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Details</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Requested</th>
                <th className="px-3 py-2 font-medium">Lifecycle</th>
                {isAdmin && <th className="px-3 py-2 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const status = r.getStatus()
                const tone = statusTone(status)
                const requestId = r.getId()
                return (
                  <tr key={requestId} style={{ borderTop: `1px solid ${DARK}`, color: GREY_200 }}>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: GREY_400 }}>
                      {r.getAccountId()}
                      {/* ADR-186 Phase 2: org/requester attribution, shown
                          only when present -- populated for source="app"
                          rows (org_id/requester_email, migration 0018);
                          absent for portal rows (identity is the account
                          alone). */}
                      {(r.getOrgId() || r.getRequesterEmail()) && (
                        <div className="mt-0.5 normal-case" style={{ color: GREY_400 }}>
                          {r.getOrgId() && <div title="org_id (attribution only)">org: {r.getOrgId()}</div>}
                          {r.getRequesterEmail() && <div>{r.getRequesterEmail()}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">{kindLabel(r.getKind())}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        // r.getSource() is proto3-empty ("") only for a row
                        // read via a fixture/older client that never set
                        // the field -- the DB column itself is
                        // NOT NULL DEFAULT 'portal' (migration 0018), so an
                        // empty wire value means "portal" here, distinct
                        // from sourceLabel('')'s "All sources" meaning in
                        // the filter dropdown above.
                        const source = r.getSource() || 'portal'
                        return (
                          <span
                            className="rounded-full px-2 py-0.5 text-xs"
                            style={{ backgroundColor: DARK, color: source === 'app' ? STATUS_COLOR.info : GREY_300 }}
                          >
                            {sourceLabel(source)}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="max-w-sm truncate px-3 py-2 font-mono text-xs" style={{ color: GREY_400 }} title={r.getPayloadJson()}>
                      {r.getPayloadJson()}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className="rounded-full px-2 py-0.5 text-xs"
                        style={{ backgroundColor: DARK, color: STATUS_COLOR[tone] }}
                      >
                        {statusLabel(status)}
                      </span>
                    </td>
                    <td className="px-3 py-2" style={{ color: GREY_400 }}>{formatTimestamp(r.getCreatedAt())}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: GREY_400 }}>
                      {r.getAckedBy() && (
                        <div>acked {formatTimestamp(r.getAckedAt())}</div>
                      )}
                      {r.getResolvedBy() && (
                        <div>resolved {formatTimestamp(r.getResolvedAt())}</div>
                      )}
                      {r.getResolutionNote() && (
                        <div className="mt-1 max-w-xs truncate italic" title={r.getResolutionNote()}>
                          “{r.getResolutionNote()}”
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={status !== 'pending' || ackMutation.isPending}
                            onClick={() => ackMutation.mutate(requestId)}
                            className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                            style={{ color: GREY_300 }}
                          >
                            Acknowledge
                          </button>
                          <ConfirmDialog
                            trigger={(open) => (
                              <button
                                type="button"
                                disabled={status !== 'acknowledged'}
                                onClick={open}
                                className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                                style={{ color: GREY_300 }}
                              >
                                Resolve
                              </button>
                            )}
                            title="Resolve request"
                            description="Mark this request resolved. You may optionally leave a note for the record — it is visible to your team, not the customer."
                            confirmLabel="Resolve"
                            onConfirm={async () => {
                              await resolveMutation.mutateAsync({
                                requestId,
                                note: resolveNoteDraft[requestId] ?? '',
                              })
                            }}
                          >
                            <label htmlFor={`resolve-note-${requestId}`} className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
                              Note (optional)
                            </label>
                            <textarea
                              id={`resolve-note-${requestId}`}
                              value={resolveNoteDraft[requestId] ?? ''}
                              onChange={(e) =>
                                setResolveNoteDraft((prev) => ({ ...prev, [requestId]: e.target.value }))
                              }
                              className="glass-input mb-3 w-full px-3 py-2 text-sm"
                              style={{ color: WHITE, minHeight: '72px' }}
                            />
                          </ConfirmDialog>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
